import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, Home, Users, UserPlus } from 'lucide-react';
import { useLifeStory } from '../contexts/LifeStoryContext';

export default function SectionsScreen() {
  const navigate = useNavigate();
  const { 
    lifeStory,
    residentName, 
    getSectionProgress, 
    getProgress, 
    goToSection,
    storyId
  } = useLifeStory();
  
  const sections = getSectionProgress();
  const progress = getProgress();

  const handleSectionClick = (sectionId) => {
    goToSection(sectionId);
    navigate(`/story/${storyId}`);
  };

  // Get display name for couples
  const getDisplayName = () => {
    if (lifeStory?.storyType === 'couple') {
      const name1 = lifeStory.preferredName || lifeStory.residentName;
      const name2 = lifeStory.secondPerson?.preferredName || lifeStory.secondPerson?.name;
      return `${name1} & ${name2}`;
    }
    return residentName;
  };

  // Get mode icon
  const getModeIcon = () => {
    if (lifeStory?.storyType === 'couple') return Users;
    if (lifeStory?.hasInterviewer) return UserPlus;
    return null;
  };

  const ModeIcon = getModeIcon();

  return (
    <div className="min-h-screen bg-gray-50 safe-area-top safe-area-bottom">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(`/story/${storyId}`)}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="font-semibold text-gray-900 flex items-center gap-2">
            {ModeIcon && <ModeIcon className="w-4 h-4 text-quiltt-navy" />}
            {getDisplayName()}'s Life Story
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="p-2 -mr-2 hover:bg-gray-100 rounded-lg"
            title="Save & Exit"
          >
            <Home className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </header>
      
      {/* Progress summary */}
      <div className="bg-white border-b px-4 py-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Overall Progress</span>
            <span className="text-sm font-medium text-gray-900">
              {progress.completionPercentage}%
            </span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-quiltt-navy transition-all duration-500"
              style={{ width: `${progress.completionPercentage}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            {progress.answeredCount} of {progress.totalQuestions} questions answered
          </p>
        </div>
      </div>
      
      {/* Section list */}
      <div className="max-w-2xl mx-auto p-4">
        <div className="space-y-2">
          {sections.map((section) => (
            <button
              key={section.sectionId}
              onClick={() => handleSectionClick(section.sectionId)}
              className="w-full bg-white rounded-xl p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors border border-gray-200"
            >
              <div className="text-2xl">{section.icon}</div>
              
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">{section.title}</h3>
                  {section.optional && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                      Optional
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  {section.description}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-quiltt-navy transition-all"
                      style={{ 
                        width: `${(section.answeredQuestions / section.totalQuestions) * 100}%` 
                      }}
                    />
                  </div>
                  <span className="text-xs text-gray-500">
                    {section.answeredQuestions}/{section.totalQuestions}
                  </span>
                </div>
              </div>
              
              {section.isComplete ? (
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
